import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from '../../../../auth/useAuth0';
import { useAlert } from "../../../../context/alert/AlertContext";
import { useConfirm } from "../../../../context/confirm/ConfirmContext";
import { useBusinessContext } from "../../../../context/BusinessContext";
import LoadingComponent from "../../../commons/LoadingComponent";
import { TeamMember } from "../../../../model/teammember/TeamMember";
import { getTeamMembersByBusinessId } from "../../../../service/teammember/team-member-service";
import {
    BusinessAdmin,
    getBusinessAdmins,
    grantTeamMemberAdmin,
    revokeTeamMemberAdmin,
} from "../../../../service/admin/business-admin-service";
import "./AdminComponent.css";

/**
 * Settings → Admin. Lists the owner-level admins of the selected business and
 * lets you promote a team member to admin or demote one. The business owner is
 * a permanent admin (never removable) — the backend guarantees a business can
 * never end up with no admin.
 */
const AdminComponent: React.FC = () => {
    const { t } = useTranslation();
    const confirmDialog = useConfirm();
    const { getAccessTokenSilently } = useAuth0();
    const { setAlert } = useAlert();
    const { selectedBusiness } = useBusinessContext();
    const businessId = selectedBusiness?.id;

    const [admins, setAdmins] = useState<BusinessAdmin[]>([]);
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [memberToPromote, setMemberToPromote] = useState<string>("");

    const load = useCallback(async () => {
        if (!businessId) {
            setAdmins([]);
            setTeamMembers([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const token = await getAccessTokenSilently();
            const [adminList, members] = await Promise.all([
                getBusinessAdmins(businessId, token),
                getTeamMembersByBusinessId(businessId, token),
            ]);
            setAdmins(adminList);
            setTeamMembers(members);
        } catch (e) {
            setAlert("danger", e instanceof Error ? e.message : t('settings.admin.alerts.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, [businessId, getAccessTokenSilently, setAlert]);

    useEffect(() => {
        load();
    }, [load]);

    const fullName = (a: { firstName?: string | null; lastName?: string | null; email?: string | null }) =>
        `${a.firstName ?? ""} ${a.lastName ?? ""}`.trim() || a.email || "—";

    const initials = (a: { firstName?: string | null; lastName?: string | null; email?: string | null }) => {
        const base = fullName(a);
        const parts = base.split(/\s+/);
        return (parts.length > 1 ? parts[0][0] + parts[1][0] : base.slice(0, 2)).toUpperCase();
    };

    // Team members eligible to become admins: not already an admin.
    const promotable = teamMembers.filter((tm) => !tm.isAdmin);

    const handleGrant = async () => {
        if (!businessId || !memberToPromote) return;
        setBusyId(memberToPromote);
        try {
            const token = await getAccessTokenSilently();
            await grantTeamMemberAdmin(businessId, memberToPromote, token);
            setAlert("success", t('settings.admin.alerts.promoted'));
            setMemberToPromote("");
            await load();
        } catch (e) {
            setAlert("danger", e instanceof Error ? e.message : t('settings.admin.alerts.promoteFailed'));
        } finally {
            setBusyId(null);
        }
    };

    const handleRevoke = async (admin: BusinessAdmin) => {
        if (!businessId || !admin.teamMemberId) return;
        if (!(await confirmDialog({ title: t('common.confirmTitle'), message: t('settings.admin.confirmRevoke', { name: fullName(admin) }), danger: true, confirmLabel: t('common.confirmTitle') }))) return;
        setBusyId(String(admin.teamMemberId));
        try {
            const token = await getAccessTokenSilently();
            await revokeTeamMemberAdmin(businessId, admin.teamMemberId, token);
            setAlert("success", t('settings.admin.alerts.removed'));
            await load();
        } catch (e) {
            setAlert("danger", e instanceof Error ? e.message : t('settings.admin.alerts.removeFailed'));
        } finally {
            setBusyId(null);
        }
    };

    if (!businessId) {
        return <p className="text-muted fst-italic p-3">{t('settings.admin.selectBusiness')}</p>;
    }

    return (
        <div className="admin-page p-3" data-cy="settings-admin-page">
            <div className="section-header mb-1">
                <h4 className="mb-1">{t('settings.admin.sectionTitle')}</h4>
            </div>
            <p className="text-muted small mb-4" style={{ maxWidth: 620 }}>
                {t('settings.admin.sectionHint')}
            </p>

            {loading ? (
                <LoadingComponent />
            ) : (
                <>
                    <div className="admin-list mb-4">
                        {admins.map((a) => (
                            <div
                                className="admin-card"
                                key={a.teamMemberId ?? `owner-${a.userId}`}
                                data-cy={`settings-admin-row-${a.teamMemberId ?? a.userId}`}
                            >
                                <div className="admin-card__avatar">{initials(a)}</div>
                                <div className="admin-card__body">
                                    <div className="admin-card__name">
                                        {fullName(a)}
                                        <span
                                            className={`admin-badge ${a.type === "OWNER" ? "admin-badge--owner" : "admin-badge--admin"}`}
                                        >
                                            {a.type === "OWNER" ? t('settings.admin.typeOwner') : t('settings.admin.typeAdmin')}
                                        </span>
                                    </div>
                                    {a.email && <div className="admin-card__email">{a.email}</div>}
                                </div>
                                <div className="admin-card__actions">
                                    {a.removable ? (
                                        <button
                                            className="btn btn-outline-danger btn-sm"
                                            data-cy={`settings-admin-revoke-btn-${a.teamMemberId}`}
                                            onClick={() => handleRevoke(a)}
                                            disabled={busyId === String(a.teamMemberId)}
                                        >
                                            {busyId === String(a.teamMemberId)
                                                ? t('settings.admin.removing')
                                                : t('settings.admin.removeAdmin')}
                                        </button>
                                    ) : (
                                        <span className="admin-card__permanent">{t('settings.admin.permanent')}</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="admin-add">
                        <label className="form-label mb-1">{t('settings.admin.addAdmin')}</label>
                        <div className="d-flex gap-2" style={{ maxWidth: 480 }}>
                            <select
                                className="form-select"
                                data-cy="settings-admin-promote-select"
                                value={memberToPromote}
                                onChange={(e) => setMemberToPromote(e.target.value)}
                                disabled={promotable.length === 0}
                            >
                                <option value="">
                                    {promotable.length === 0
                                        ? t('settings.admin.allAdmins')
                                        : t('settings.admin.selectMember')}
                                </option>
                                {promotable.map((tm) => (
                                    <option key={tm.id} value={tm.id}>
                                        {tm.firstName} {tm.lastName}
                                        {tm.email ? ` — ${tm.email}` : ""}
                                    </option>
                                ))}
                            </select>
                            <button
                                className="btn btn-primary"
                                data-cy="settings-admin-promote-btn"
                                onClick={handleGrant}
                                disabled={!memberToPromote || busyId === memberToPromote}
                            >
                                {busyId === memberToPromote
                                    ? t('settings.admin.adding')
                                    : t('settings.admin.makeAdmin')}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminComponent;
